@if (type === 'section') {
  <dcr-section
    standout
    heading-level="h4"
    [headline]="headline"
    [supportingText]="supportingText"
    [open]="open"
    (opened)="onOpenChange($event)"
    (closed)="onCloseChange($event)"
    >
    <ng-container *ngTemplateOutlet="heroTemplate"></ng-container>
    <ng-content></ng-content>
    <ng-container *ngTemplateOutlet="metaTemplate"></ng-container>
  </dcr-section>
}

@if (type === 'listitem') {
  <dcr-list-item
    class="w-full"
    [type]="actions?.length ? 'listitem' : 'button'"
    [headline]="headline"
    [supportingText]="supportingText"
    [heroType]="heroType"
    data-cy="results-summary"
    >
    <ng-container *ngTemplateOutlet="heroTemplate"></ng-container>
    <ng-container *ngTemplateOutlet="metaTemplate"></ng-container>
    @if (result != null && actions?.length) {
      <div slot="trailing-actions">
        @if (actions?.length > 1) {
          <div>
            <dcr-inline-icon-button
              data-cy="edu-content-result-action"
              [attr.id]="'more-actions-' + id"
              icon="shared:more"
            ></dcr-inline-icon-button>
            <dcr-menu [anchor]="'more-actions-' + id" positioning="popover">
              @for (action of actions; track action) {
                <dcr-menu-item
                  [disabled]="action.disabled"
                  [leadingIcon]="'content-open-actions:' + action.icon"
                  [label]="action.label"
                  (click)="action.handler(eduContent, { forResult: result })"
                ></dcr-menu-item>
              }
            </dcr-menu>
          </div>
        } @else {
          @if (actions[0]; as action) {
            <dcr-tonal-button
              data-cy="edu-content-result-action"
              [disabled]="action.disabled"
              [leadingIcon]="'content-open-actions:' + action.icon"
              [label]="action.label"
              (click)="action.handler(eduContent, { forResult: result })"
            ></dcr-tonal-button>
          }
        }
      </div>
    }
  </dcr-list-item>
}

<ng-template #heroTemplate>
  @switch (heroView) {
    <!-- Person avatar -->
    @case ('avatar') {
      <dcr-avatar
        [attr.slot]="type === 'listitem' ? 'hero' : 'leading-image'"
        [imageUrl]="person?.avatar"
        [attr.firstname]="person?.firstName"
        [attr.lastname]="person?.name"
        [imageAlt]="person?.displayName"
        [attr.show-initials]="!person?.avatar ? '' : undefined"
      ></dcr-avatar>
    }
    <!-- Score (single result) -->
    @case ('score') {
      <campus-score
        [attr.slot]="type === 'listitem' ? 'hero' : 'leading-image'"
        data-cy="pages-results-edu-content-score"
        [class.cursor-pointer]="
          result.status === 'completed' && (result.data.needsManualGrading || (result.data.score && result.isGradable))
        "
        [result]="result.data"
        [attempts]="result?.data?.attempts"
        [manualGrading]="result.data?.needsManualGrading || (result.data?.score !== null && result.isGradable)"
        (click)="
          result.status === 'completed'
            ? result.actions[1]?.handler(result?.eduContent, { forResult: { ...result, ...result.data } })
            : undefined
        "
      ></campus-score>
    }
    @case ('sessionDay') {
      @if (result.attempts.length === 1) {
        <campus-score-level
          [attr.slot]="type === 'listitem' ? 'hero' : 'leading-image'"
          [scoreLevel]="result.attempts[0]"
        ></campus-score-level>
      } @else {
        <campus-score-level-attempts
          [attr.slot]="type === 'listitem' ? 'hero' : 'leading-image'"
          [scoreLevels]="result.attempts"
        ></campus-score-level-attempts>
      }
    }
    @case ('session') {
      <campus-score-level
        [attr.slot]="type === 'listitem' ? 'hero' : 'leading-image'"
        [scoreLevel]="result.data"
      ></campus-score-level>
    }
    <!-- Score scatter -->
    @case ('scatter') {
      <div [attr.slot]="type === 'listitem' ? 'hero' : 'leading-image'">
        <campus-score-scatter [results]="results" [maxAmountScores]="resultsMaxAmount"></campus-score-scatter>
      </div>
    }
    <!-- Score scatter V2 -->
    @case ('scatterV2') {
      <div [attr.slot]="type === 'listitem' ? 'hero' : 'leading-image'">
        <campus-score-scatter-v2 [spread]="total.spread"></campus-score-scatter-v2>
      </div>
    }
    <!-- Ability (goal) -->
    @case ('ability') {
      <div [attr.slot]="type === 'listitem' ? 'hero' : 'leading-image'">
        @if (!result?.drillAbility) {
          @if (result?.score != null && result?.score >= 0) {
            <dcr-circular-score
              [value]="result!.score"
              has-label
            ></dcr-circular-score>
          } @else {
            <dcr-circular-score indeterminate></dcr-circular-score>
          }
        } @else {
          @if (result?.score != null && result?.score >= 0) {
            <dcr-segmented-circular-progress
              [attr.low-value]="result?.drillAbility?.easy"
              [attr.medium-value]="result?.drillAbility?.medium"
              [attr.high-value]="result?.drillAbility?.hard"
            ></dcr-segmented-circular-progress>
          } @else {
            <dcr-circular-score indeterminate></dcr-circular-score>
          }
        }
      </div>
    }
    <!-- Optional: nothing to show -->
    @default {
    }
  }
</ng-template>

<ng-template #amountTemplate let-amount="amount" let-type="type" let-singular="singular" let-plural="plural">
  <div class="relative" [attr.data-cy]="'results-summary-' + type + '-amount'">
    <dcr-icon
      [attr.id]="type + '-amount-' + id"
      [icon]="'shared:' + type"
      [attr.aria-label]="'Komt in ' + amount + (amount === 1 ? ' ' + singular : ' ' + plural) + ' voor.'"
    ></dcr-icon>
    <dcr-badge [count]="amount" class="absolute inset-bs-0 inset-ie-0 transform -translate-y-1/2 translate-x-1/2"></dcr-badge>
    <dcr-plain-tooltip
      positioning="popover"
      [anchor]="type + '-amount-' + id"
      [label]="'in ' + amount + (amount === 1 ? ' ' + singular : ' ' + plural)"
    ></dcr-plain-tooltip>
  </div>
</ng-template>

<ng-template #summary let-value="value" let-label="label">
  <div class="text-center" [attr.data-cy]="'results-summary-' + label + '-count'">
    <div class="label-large on-surface">{{ value }}</div>
    <div class="label-small on-surface-variant">{{ label }}</div>
  </div>
</ng-template>

<ng-template #metaTemplate>
  <div class="flex items-center gap-xs" [slot]="type === 'listitem' ? 'trailing-content' : 'trailing-image'">
    @if (cappedAssignees) {
      <campus-capped-chip data-cy="results-summary-assignees" [chips]="cappedAssignees"></campus-capped-chip>
    }

    @if (studentAmount != null) {
      <ng-container
        *ngTemplateOutlet="summary; context: { value: studentAmount, label: studentAmount === 1 ? 'leerling' : 'leerlingen' }"
      ></ng-container>
    }

    @if (eduContentAmount != null) {
      <ng-container
        *ngTemplateOutlet="summary; context: { value: eduContentAmount, label: eduContentAmount === 1 ? 'oefening' : 'oefeningen' }"
      ></ng-container>
    }

    @if (practicesAmount != null) {
      <ng-container *ngTemplateOutlet="summary; context: { value: practicesAmount, label: 'x geoefend' }"></ng-container>
    }

    @if (interactionAmount != null) {
      <ng-container
        *ngTemplateOutlet="summary; context: { value: interactionAmount, label: interactionAmount === 1 ? 'interactie' : 'interacties' }"
      ></ng-container>
    }

    @if (timeSpent != null) {
      <ng-container
        *ngTemplateOutlet="summary; context: { value: timeSpent | humanDateTime: { format: 'duration' }, label: 'gewerkt' }"
      ></ng-container>
    }

    @if (level != null) {
      <div>
        <dcr-icon [attr.id]="'level-' + id" [icon]="'educontent:level:' + level.value" [attr.aria-label]="level.value"></dcr-icon>
        <dcr-plain-tooltip positioning="popover" [anchor]="'level-' + id" [label]="level.name"></dcr-plain-tooltip>
      </div>
    }

    @if (eduContentType != null) {
      <div>
        <dcr-icon [attr.id]="'filetype-' + id" [icon]="'educontent:file-type:' + eduContentType.icon"></dcr-icon>
        <dcr-plain-tooltip positioning="popover" [anchor]="'filetype-' + id" [label]="eduContentType.label"></dcr-plain-tooltip>
      </div>
    }

    @if (source) {
      @if (source.id === 'kai') {
        <div>
          <dcr-animated-kai [attr.id]="'kai-' + id" [src]="'kai_state_machine.riv' | assetsPath: 'rive'"></dcr-animated-kai>
          <dcr-plain-tooltip positioning="popover" [anchor]="'kai-' + id" [label]="[source.tooltip]"></dcr-plain-tooltip>
        </div>
      } @else {
        <div data-cy="results-summary-in-instance">
          <dcr-icon [attr.id]="'in-' + source.id + '-' + id" [icon]="'shared:' + source.icon" [attr.aria-label]="source.label"></dcr-icon>
          <dcr-plain-tooltip positioning="popover" [anchor]="'in-' + source.id + '-' + id" [label]="source.tooltip"></dcr-plain-tooltip>
        </div>
      }
    }

    @if (taskAmount) {
      <ng-container
        *ngTemplateOutlet="
          amountTemplate;
          context: {
            amount: taskAmount,
            type: 'task',
            singular: 'taak',
            plural: 'taken',
          }
        "
    ></ng-container>
  }

  @if (bundleAmount) {
    <ng-container
        *ngTemplateOutlet="
          amountTemplate;
          context: {
            amount: bundleAmount,
            type: 'bundle',
            singular: 'bundel',
            plural: 'bundels',
          }
        "
  ></ng-container>
}
</div>
</ng-template>
